<template>
  <v-container fluid grid-list-xl>
    <v-layout row wrap mb-2 class="grey lighten-4">
      <h2>Terminals</h2>
    </v-layout>

    <div>
      <v-data-table
        :headers="columnsDisplayed"
        :items="rows"
        :pagination.sync="pagination"
        :loading="loading"
        :total-items="totalRows"
        :rows-per-page-items="pageCountOptions"
        item-key="id"
        class="elevation-1"
        must-sort
        no-data-text="No data found"
        no-results-text="No results found"
        @update:pagination="search"
      >
        <template slot="headers" slot-scope="props">
          <tr>
            <th
              v-for="(header) in props.headers"
              :key="header.value"
              v-show="header.visible"
              :class="['font-weight-bold text-xs-left column sortable pa-1 ma-0', pagination.descending ? 'desc' : 'asc', header.value === pagination.sortBy ? 'active' : '']"
              @click="changeSort(header.value)"
            >
              {{ header.text }}
              <v-icon small>arrow_upward</v-icon>
            </th>
            <th>Duplicate</th>
            <th class="ma-0 pa-0 text-xs-right">
              <v-spacer></v-spacer>
              <v-btn icon flat color="primary" @click="reset">
                <v-icon>fa-refresh</v-icon>
              </v-btn>
            </th>
          </tr>
        </template>

        <template slot="items" slot-scope="props">
          <tr>
            <td
              v-for="(header) in columnsDisplayed"
              :key="header.value"
              v-show="header.visible"
              class="pa-1 ma-0"
              :class="(header.type === 'boolean') ? 'text-xs-center' : ''"
            >
              <template v-if="header.type === 'boolean'">
                <span v-if="props.item[header.value]">Yes</span>
                <span v-else>No</span>
              </template>

              <template v-else-if="header.type === 'money'">
                {{ moneyNoSign(props.item[header.value] / 100) }}
              </template>

              <template v-else-if="header.type === 'date'">
                {{ dateUtc(props.item[header.value]) }}
              </template>

              <template v-else class="text-capitalize">
                {{ props.item[header.value] }}
              </template>
            </td>
            <td class="text-xs-center">
              <v-btn icon flat color="primary" @click="openDuplicateModal(props.item)" title="Duplicate">
                <v-icon>fa-copy</v-icon>
              </v-btn>
            </td>
            <td class="ma-0 pa-0 text-xs-right">
              <v-btn round small :to="{ name: 'Terminal', params: { id: props.item.id }}">View</v-btn>
            </td>
          </tr>
        </template>

        <template slot="no-data">
          No results found
        </template>
      </v-data-table>

      <v-layout row wrap justify-end pr-1 class="mt-3">
        <v-btn color="primary" outline :to="{ name: 'TerminalAdd', query: accountId ? { account_id: accountId } : {} }">
          <v-icon left>fa-plus</v-icon>
          Create
        </v-btn>
        <v-btn color="primary" class="mb-2" @click="searchModal = true">
          <v-icon left>fa-search</v-icon>
          Search
        </v-btn>
      </v-layout>
    </div>

    <v-dialog v-model="duplicateModal" max-width="75vw" @keydown.esc.prevent="duplicateModal = false">
      <v-toolbar dense>
        <v-toolbar-title>Duplicate Terminal</v-toolbar-title>
        <v-spacer></v-spacer>
        <v-btn icon flat @click="duplicateModal = false">
          <v-icon>fa-close</v-icon>
        </v-btn>
      </v-toolbar>

      <v-card>
        <v-card-text class="pa-4">
          <v-alert type="info" :value="true" outline class="mb-3">
            Select how many terminals to duplicate. Each terminal's <strong>Primary Name</strong> is pre-filled from the original and is locked by default — click the lock button to edit it. The <strong>Secondary Name</strong> is required when the Primary Name is unchanged, and will be appended to form the final name (e.g. <em>Primary Name - Secondary Name</em>). If the Primary Name is changed, Secondary Name is optional.
          </v-alert>
          <div class="mb-3">Duplicating <strong>{{ duplicateTarget ? duplicateTarget.name : '' }}</strong></div>
          <v-layout row align-center class="mb-4">
            <span class="subheading mr-3">Select 1 or More:</span>
            <v-text-field
              v-model.number="duplicateCount"
              type="number"
              min="1"
              max="20"
              box
              hide-details
              style="max-width: 80px;"
              @change="onCountChange"
            />
          </v-layout>
          <v-divider class="primary my-3" />
          <fieldset
            v-for="(item, index) in duplicateItems"
            :key="index"
            class="duplicate-fieldset"
            :class="index < duplicateItems.length - 1 ? 'mb-3' : ''"
          >
            <legend class="duplicate-fieldset__legend">Terminal {{ index + 1 }}</legend>
            <v-layout row align-center class="mb-3">
              <v-flex>
                <v-text-field
                  v-model="item.primaryName"
                  box
                  :hide-details="!item.primaryNameError"
                  :error-messages="item.primaryNameError ? [item.primaryNameError] : []"
                  :readonly="!item.editingPrimaryName"
                  :class="item.editingPrimaryName ? 'primary-name--unlocked' : 'primary-name--locked'"
                  @input="item.primaryNameError = null"
                >
                  <template slot="label">Primary Name <span class="red--text">*</span></template>
                </v-text-field>
              </v-flex>
              <v-btn flat :color="item.editingPrimaryName ? 'success' : 'error'" class="ml-2" style="width: 120px;" @click="item.editingPrimaryName = !item.editingPrimaryName">
                <v-icon left>{{ item.editingPrimaryName ? 'fa-unlock' : 'fa-lock' }}</v-icon>
                {{ item.editingPrimaryName ? 'Unlocked' : 'Locked' }}
              </v-btn>
            </v-layout>
            <v-text-field
              :label="duplicateTarget && item.primaryName.trim() === duplicateTarget.name ? 'Secondary Name *' : 'Secondary Name'"
              v-model="item.secondaryName"
              box
              :hide-details="!item.secondaryNameError"
              :error-messages="item.secondaryNameError ? [item.secondaryNameError] : []"
              @input="item.secondaryNameError = null"
            />
          </fieldset>
          <v-progress-linear v-if="duplicateCountLoading" indeterminate color="primary" class="mt-3 mb-0" />
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn flat @click="duplicateModal = false">Cancel</v-btn>
          <v-btn color="primary" :loading="duplicating" :disabled="duplicateCountLoading || duplicateItems.length === 0" @click="confirmDuplicate">Duplicate</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="searchModal" @submit.prevent @keydown.esc.prevent="searchModal=false">
      <v-toolbar dense>
        <v-toolbar-title>Search</v-toolbar-title>
        <v-spacer></v-spacer>
        <v-btn icon flat color="primary" @click.native="searchModal = false">
          <v-icon>fa-close</v-icon>
        </v-btn>
      </v-toolbar>

      <v-card>
        <v-card-text>
          <v-form @keyup.native.enter="searchNew">
            <v-container fluid>
              <v-layout row wrap>
                <v-flex xs6 sm3>
                  <v-text-field
                    label="Name"
                    v-model="filter.name"
                    box
                    hide-details
                  />
                </v-flex>
                <v-flex xs6 sm3>
                  <v-text-field
                    label="Code"
                    v-model="filter.code"
                    box
                    hide-details
                  />
                </v-flex>
                <v-flex xs6 sm3>
                  <v-text-field
                    label="Serial"
                    v-model="filter.serial_number"
                    box
                    hide-details
                  />
                </v-flex>
                <v-flex xs6 sm3>
                  <v-autocomplete
                    name="account"
                    label="Account"
                    v-model="filter.account_id"
                    :items="accounts"
                    item-text="name"
                    item-value="account_id"
                    box
                    hide-details
                    v-if="myProfile.accounts.length > 1"
                  />
                </v-flex>
              </v-layout>

              <v-divider class="primary" />

              <v-layout row wrap justify-end>
                <v-btn color="primary" @click="search">Search</v-btn>
                <v-btn @click="reset">Clear</v-btn>
              </v-layout>
            </v-container>
          </v-form>
        </v-card-text>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import Moment from 'moment'

import Bus from '@/bus'
import ApiMixin from '@/mixins/api'
import AuthMixin from '@/mixins/auth'
import DisplayMixin from '@/mixins/display'
import StoreMixin from '@/store/mixin'
import UtilityMixin from '@/mixins/utility'

export default {
  mixins: [ApiMixin, AuthMixin, DisplayMixin, StoreMixin, UtilityMixin],

  data: function () {
    return {
      booleanSelect: [
        {value: '*', text: 'All'},
        {value: '0', text: 'No'},
        {value: '1', text: 'Yes'}
      ],

      columns: [
        {
          value: 'name',
          text: 'Name',
          sortable: true,
          visible: true,
          export: true
        },
        {
          value: 'code',
          text: 'Code',
          sortable: true,
          visible: true,
          export: true
        },
        {
          value: 'type',
          text: 'Type(s)',
          sortable: true,
          visible: true,
          export: true
        },
        {
          value: 'is_virtual',
          text: 'Virtual',
          sortable: true,
          visible: true,
          export: true,
          type: 'boolean'
        },
        {
          value: 'password',
          text: 'Password',
          sortable: true,
          visible: true,
          export: true
        },
        {
          value: 'api',
          text: 'API',
          sortable: true,
          visible: true,
          export: true
        },
        {
          value: 'account_name',
          text: 'Account',
          sortable: true,
          visible: true,
          export: true
        },
        {
          value: 'processor_name',
          text: 'Processor',
          sortable: true,
          visible: true,
          export: true
        },
        {
          value: 'key',
          text: 'Key',
          sortable: true,
          visible: true,
          export: true
        },
        {
          value: 'active',
          text: 'Active',
          sortable: true,
          visible: true,
          export: true,
          type: 'boolean'
        },
        {
          value: 'serial_number',
          text: 'Serial',
          sortable: true,
          visible: true,
          export: true
        },
        {
          value: 'created',
          text: 'Created',
          sortable: true,
          visible: true,
          export: true,
          type: 'date'
        },
        {
          value: 'modified',
          text: 'Modified',
          sortable: true,
          visible: true,
          export: true,
          type: 'date'
        }
      ],
      pagination: this.defaultPagination(),
      rows: [],
      totalRows: 0,
      pageCountOptions: [5, 10, 25],
      loading: true,

      searchModal: false,
      createdModal: false,
      modifiedModal: false,
      duplicateModal: false,
      duplicateTarget: null,
      duplicateCount: 1,
      duplicateItems: [],
      duplicateCountLoading: false,
      duplicating: false,

      filter: this.defaultFilter()
    }
  },

  props: {
    accountId: Number,
    userId: Number
  },

  computed: {
    environment () {
      return process.env.NODE_ENV
    },

    accounts: function () {
      let accounts = [
        {account_id: '*', name: 'All'}
      ]
      return accounts.concat(this.myProfile.accounts)
    },

    columnsDisplayed: function () {
      return this.columns.filter(column => {
        if (this.myProfile.custom && this.myProfile.custom.hasOwnProperty('terminal_columns')) {
          if (this.myProfile.custom.transaction_columns.includes(column.value)) {
            column.visible = true
          } else {
            column.visible = false
          }
        }

        if (column.visible) {
          return true
        }

        return false
      }, this)
    },

    formattedCreatedStartDate () {
      return this.filter.created_start_date ? Moment(this.filter.created_start_date).format('MMM-DD-YYYY') : ''
    },
    formattedCreatedEndDate () {
      return this.filter.created_end_date ? Moment(this.filter.created_end_date).format('MMM-DD-YYYY') : ''
    },
    formattedModifiedStartDate () {
      return this.filter.modified_start_date ? Moment(this.filter.modified_start_date).format('MMM-DD-YYYY') : ''
    },
    formattedModifiedEndDate () {
      return this.filter.modified_end_date ? Moment(this.filter.modified_end_date).format('MMM-DD-YYYY') : ''
    }
  },

  methods: {
    defaultFilter () {
      return {
        name: '',
        code: '',
        serial_number: '',
        account_id: this.accountId ? this.accountId : '*',
        user_id: this.userId ? this.userId : '*'
      }
    },

    defaultPagination () {
      return {
        sortBy: this.$store.state.paginationDefaults.sortBy,
        descending: this.$store.state.paginationDefaults.descending,
        rowsPerPage: this.$store.state.paginationDefaults.rowsPerPage,
        page: 1
      }
    },

    searchNew () {
      this.pagination.page = 1
      this.search()
    },

    async search () {
      this.loading = true

      try {
        let filter = { ...this.filter }

        if (filter.created_start_date) {
          filter.created_start_date = Moment(this.filter.created_start_date).utc().format('YYYY-MM-DD HH:mm:ss')
        }
        if (filter.created_end_date) {
          filter.created_end_date = Moment(Moment(this.filter.created_end_date).format('YYYY-MM-DD') + ' 23:59:59').utc().format('YYYY-MM-DD HH:mm:ss')
        }
        if (filter.modified_start_date) {
          filter.modified_start_date = Moment(this.filter.modified_start_date).utc().format('YYYY-MM-DD HH:mm:ss')
        }
        if (filter.modified_end_date) {
          filter.modified_end_date = Moment(Moment(this.filter.modified_end_date).format('YYYY-MM-DD') + ' 23:59:59').utc().format('YYYY-MM-DD HH:mm:ss')
        }

        let result = await this.api({
          method: 'get',
          url: '/terminals',
          params: {
            filter: filter,
            page: this.pagination.page - 1,
            perPage: this.pagination.rowsPerPage,
            orderBy: this.pagination.sortBy,
            orderDirection: this.pagination.descending ? 'desc' : 'asc'
          }
        })
        this.rows = result.data.rows
        this.totalRows = result.data.count
        this.searchModal = false
      } catch (err) {
        Bus.$emit('openModal', {
          status: 'error',
          message: err
        })
      } finally {
        this.loading = false
      }
    },

    reset () {
      this.filter = this.defaultFilter()
      this.pagination = this.defaultPagination()

      this.search()
    },

    async openDuplicateModal (item) {
      this.duplicateTarget = item
      this.duplicateCount = 1
      this.duplicateItems = []
      this.duplicateModal = true
      await this.addDuplicateItems(1)
    },

    async addDuplicateItems (n) {
      this.duplicateCountLoading = true
      try {
        for (let i = 0; i < n; i++) {
          const result = await this.api({ method: 'get', url: '/terminals/generate-code' })
          this.duplicateItems.push({
            primaryName: this.duplicateTarget.name,
            primaryNameError: null,
            secondaryName: '',
            secondaryNameError: null,
            editingPrimaryName: false,
            code: result.data.code
          })
        }
      } catch (err) {
        Bus.$emit('openModal', { status: 'error', message: err })
        this.duplicateModal = false
      } finally {
        this.duplicateCountLoading = false
      }
    },

    async onCountChange () {
      const count = Math.max(1, Math.min(20, parseInt(this.duplicateCount) || 1))
      this.duplicateCount = count
      if (count > this.duplicateItems.length) {
        await this.addDuplicateItems(count - this.duplicateItems.length)
      } else {
        this.duplicateItems = this.duplicateItems.slice(0, count)
      }
    },

    async confirmDuplicate () {
      // Validate all identifier fields
      let hasErrors = false
      this.duplicateItems.forEach((item, index) => {
        if (!item.primaryName || !item.primaryName.trim()) {
          item.primaryNameError = `Terminal ${index + 1} Primary Name is required`
          hasErrors = true
        } else {
          item.primaryNameError = null
        }

        const primaryChanged = item.primaryName.trim() !== this.duplicateTarget.name
        if (!primaryChanged && (!item.secondaryName || !item.secondaryName.trim())) {
          item.secondaryNameError = `Terminal ${index + 1} Secondary Name is required when Primary Name is unchanged`
          hasErrors = true
        } else {
          item.secondaryNameError = null
        }
      })
      if (hasErrors) return

      this.duplicating = true
      try {
        for (const item of this.duplicateItems) {
          await this.api({
            method: 'post',
            url: `/terminals/${this.duplicateTarget.id}/duplicate`,
            data: { name: item.secondaryName.trim() ? `${this.sanitizeText(item.primaryName)} - ${this.sanitizeText(item.secondaryName)}` : this.sanitizeText(item.primaryName), code: item.code }
          })
        }
        this.duplicateModal = false
        this.duplicateTarget = null
        this.duplicateCount = 1
        this.duplicateItems = []
        await this.search()
      } catch (err) {
        Bus.$emit('openModal', {
          status: 'error',
          message: err
        })
      } finally {
        this.duplicating = false
      }
    },

    changeSort (column) {
      if (this.pagination.sortBy === column) {
        this.pagination.descending = !this.pagination.descending
      } else {
        this.pagination.sortBy = column
        this.pagination.descending = false
      }
      this.search()
    }
  }
}
</script>

<style>
.duplicate-fieldset {
  border: 1px solid #bdbdbd;
  border-radius: 4px;
  padding: 10px 16px 16px;
  margin: 8px 32px;
}
.duplicate-fieldset__legend {
  font-size: 12px;
  font-weight: 600;
  color: rgba(0, 0, 0, 0.54);
  padding: 0 4px;
}
.duplicate-fieldset .v-input--box .v-input__slot {
  background-color: #fff !important;
}
.primary-name--locked .v-input__slot {
  background-color: #bdbdbd !important;
}
</style>


// WEBPACK FOOTER //
// src/components/manage/Terminals.vue